import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { isApiError } from '@repletoryreach/sdk-api';
import { Stack, Button, Text, CopyButton } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { AuthCardHeader } from '../components/AuthCardHeader';
import { ArrowLeftIcon, ShieldIcon } from '../components/icons';
import { OneTimeSecret } from '../../settings/components/OneTimeSecret';
import { useT } from '../../../i18n/I18nProvider';

/*
 * Two-factor set up at the first sign-in (3 October 2026). Platform staff invited from the console must
 * have it before any session is issued: the login form lands here when identity answers with a setup
 * token. The secret goes into an authenticator app, the first code switches it on, the recovery codes
 * are shown once, and only after they are saved does the console sign in.
 */
export default function MfaSetupPage() {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const { hasPendingTwoFactorSetup, startTwoFactorSetup, confirmTwoFactorSetup, finishTwoFactorSetup } = useAuth();
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  const waiting = hasPendingTwoFactorSetup();

  useEffect(() => {
    if (!waiting || secret) return;
    startTwoFactorSetup()
      .then((r) => setSecret(r.secret))
      .catch(() => setError(t('auth.mfaSetup.expired')));
  }, [waiting, secret, startTwoFactorSetup, t]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      setCodes(await confirmTwoFactorSetup(code.trim()));
    } catch (err) {
      setError(isApiError(err) && err.code === 'INVALID_CODE' ? t('auth.mfaSetup.wrongCode') : t('auth.mfaSetup.expired'));
    } finally {
      setBusy(false);
    }
  }

  async function done() {
    setCodes(null);
    await finishTwoFactorSetup();
    navigate(from, { replace: true });
  }

  return (
    <Stack gap="md">
      <AuthCardHeader title={t('auth.mfaSetup.title')} subtitle={t('auth.staffOnly')} icon={<ShieldIcon size={48} />} />
      {!waiting && !codes ? (
        <Alert color="warning"><Text fz="sm">{t('auth.mfaSetup.noSetup')}</Text></Alert>
      ) : (
        <form onSubmit={submit}>
          <Stack gap="md">
            <Text fz="sm">{t('auth.mfaSetup.intro')}</Text>
            {secret && (
              <Stack gap="xs">
                <Text role="caption" c="var(--rr-color-text-muted)">{t('auth.mfaSetup.secretLabel')}</Text>
                <code data-testid="mfa-setup-secret" style={{ wordBreak: 'break-all', userSelect: 'all' }}>{secret}</code>
                <CopyButton value={secret}>
                  {({ copied, failed, copy }) => (
                    <Button size="xs" variant="subtle" onClick={copy} style={{ alignSelf: 'flex-start' }}>
                      {failed ? t('settings.secret.copyFailed') : copied ? t('common.copied') : t('action.copy')}
                    </Button>
                  )}
                </CopyButton>
              </Stack>
            )}
            <TextInput
              label={t('auth.mfaSetup.code')}
              description={t('auth.mfaSetup.codeHint')}
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              error={error ?? undefined}
            />
            <Button type="submit" loading={busy} disabled={!secret || code.trim().length < 6}>{t('auth.mfaSetup.submit')}</Button>
          </Stack>
        </form>
      )}
      <Link to="/auth/login" className="admin-auth__row-link" replace>
        <ArrowLeftIcon />
        {t('auth.login.twoFactor.back')}
      </Link>
      <OneTimeSecret
        value={codes ? codes.join('\n') : null}
        lines={codes ?? undefined}
        title={t('settings.security.twoFactor.codes.title')}
        intro={t('settings.security.twoFactor.codes.intro')}
        filename={`repletoryreach-recovery-codes-${new Date().toISOString().slice(0, 10)}.txt`}
        onAcknowledge={() => void done()}
      />
    </Stack>
  );
}
